import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import { useBannerImpression } from '../../hooks/useBannerImpression.ts'
import AddToCartButton from './AddToCartButton.tsx'
import Rating from './Rating.tsx'
import style from './BannerCard.module.scss'

interface IBannerCardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  amount: number
  findability: boolean
  disabled?: boolean
  // An ad in the results page's banner band, or a recommendation on one of the shelves (cart,
  // detail page, added-to-cart sheet). The band lays out as a row on the phone; shelves stack.
  variant: 'banner' | 'recommendation'
  onSelect: () => void
  onAdd: (qnt: number) => void
}

const BannerCard = ({
  schema,
  answer,
  amount,
  findability,
  disabled = false,
  variant,
  onSelect,
  onAdd
}: IBannerCardProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  // A rating below one star is unreachable for a real product, so it is treated as no rating.
  const hasRating = Number(answer.rating) >= 1
  const hasVariants = useChildrenProducts({ answer, schema, excludeCurrentProduct: true }).length > 0
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  // The band is the banner placement; every recommendation shelf is the featured one, because the
  // featured flag is what fills all three of them.
  const bannerRef = useBannerImpression(answer.id, 'banner' === variant ? 'banner' : 'featured')
  const row = 'banner' === variant && viewport.mobile
  // The phone's recommendation card takes the band's type scale, stacked.
  const layoutClass = row ? style.cardRow : viewport.mobile ? style.cardMobile : ''

  const image = (
    <div className={style.imageWrapper} onClick={onSelect}>
      {answer.image ? (
        <div
          className={style.image}
          role="img"
          aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
          style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }}
        />
      ) : (
        <div className={style.noImage} />
      )}
    </div>
  )

  const cta = (
    <div className={style.cta}>
      <AddToCartButton
        amount={amount}
        findability={findability}
        limit={schema.max_qty_per_product}
        disabled={disabled}
        placement="banner"
        spread={row}
        view={hasVariants}
        onView={onSelect}
        onAdd={onAdd}
      />
    </div>
  )

  const body = (
    <div className={style.body} onClick={onSelect}>
      <div className={style.priceRow}>
        {hasDiscount ? (
          <>
            <span className={style.now}>{t('questions', 'ec', 'samsclub', 'nowPrice')}</span>
            <span className={style.priceSale} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
            <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
          </>
        ) : (
          <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        )}
        {answer.per_count_price && (
          <span
            className={style.unitPrice}
            dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
          />
        )}
      </div>
      <div className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
    </div>
  )

  // The rating is a decoration that raises the simulation notice on click, so it stays outside the
  // region that opens the product -- one gesture, one outcome.
  const rating = hasRating && (
    <div className={style.ratingRow}>
      <Rating rating={answer.rating} />
      {answer.reviews != null && <span className={style.reviews}>{answer.reviews.toLocaleString(languageTag)}</span>}
    </div>
  )

  return (
    <div
      ref={bannerRef}
      data-testid="SW-EC-BannerItem"
      aria-disabled={disabled}
      className={`${style.card} ${layoutClass} ${disabled ? style.disabled : ''}`}
    >
      {row ? (
        <>
          <div className={style.rowMedia}>
            {image}
            {cta}
          </div>
          {body}
          {rating}
        </>
      ) : (
        <>
          {image}
          {cta}
          {body}
          {rating}
        </>
      )}
    </div>
  )
}

export default BannerCard
